'use client';
import { twMerge } from 'tailwind-merge';
import { useRutilusQuery } from '../../../../api/graphql/useRutilusQuery';
import { SpeciesCard } from '../../../../components/Cards/SpeciesCard/SpeciesCard';
import { ContinuousCarousel } from '../../../../components/Carousels/ContinuousCarousel/ContinuousCarousel';
import { Container } from '../../../../components/Container/Container';
import { useGeolocation } from '../../../../contexts/GeolocationContext/GeolocationContext';
import { getEdgesFromConnection, getNodesFromConnection } from '../../../../helpers/graphql';
import { useClientHasRendered } from '../../../../helpers/hooks';
import {
  GetSpeciesFromList_species_edges,
  GetTopSpeciesNearLocation,
  GetTopSpeciesNearLocationVariables,
} from '../../../../interfaces/graphql';
import { getTopSpeciesNearLocation } from '../../../../queries/rutilus/Species';
import { useState } from 'react';
import { trackEvent } from '../../../../helpers/analytics/trackEvent';
import { useAnalytics } from '../../../../contexts/AnalyticsContext/AnalyticsContext';
import { Text } from '../../../../components/Text/Text';
import { Button } from '../../../../components/Clickables/Buttons/Button';
import { InfoIcon } from '../../../../components/Icons/InfoIcon';
import { LocationInfoModal } from '../../../../components/LocationInfoModal/LocationInfoModal';

interface IProps {
  className?: string;
  id?: string;
  title?: string;
  clickEventSourceId?: string;
  defaultSpeciesEdgeList?: GetSpeciesFromList_species_edges[];
}

const POPULAR_SPECIES_SECTION_TITLE = 'Most popular fish species near you';
const RADIUS_OF_SPECIES_IN_KM = 50;

export const NearbyPopularSpeciesSection = ({
  className,
  id = 'popular-species-near-you',
  title = POPULAR_SPECIES_SECTION_TITLE,
  clickEventSourceId,
  defaultSpeciesEdgeList = [],
}: IProps) => {
  const clientHasRendered = useClientHasRendered();
  const { currentPage } = useAnalytics();

  const sortedDefaultSpecies = defaultSpeciesEdgeList
    .map(obj => ({ ...obj, catchesCount: undefined }))
    .filter(sp => sp.node?.catches.totalCount)
    .sort((sp1, sp2) => (sp2.node?.catches.totalCount ?? 0) - (sp1.node?.catches.totalCount ?? 0));

  const { isLoading: currentLocationLoading, location } = useGeolocation();
  const [infoModalOpen, setIsInfoModalOpen] = useState(false);

  const country = location?.countryCode;
  const region = location?.regionName;

  const shouldSkip = currentLocationLoading || !location?.coordinates;

  const { data, loading } = useRutilusQuery<
    GetTopSpeciesNearLocation,
    GetTopSpeciesNearLocationVariables
  >(getTopSpeciesNearLocation, {
    variables: location?.coordinates
      ? {
          first: 8,
          boundingCircle: {
            center: location.coordinates,
            delta: RADIUS_OF_SPECIES_IN_KM,
          },
        }
      : undefined,
    skip: shouldSkip,
  });

  const isLoading = currentLocationLoading || loading;

  const speciesList = data
    ? getEdgesFromConnection(data.topSpeciesNearLocation)
    : sortedDefaultSpecies;

  const getTestId = (): string => {
    if (isLoading || !clientHasRendered) {
      return 'PopularSpeciesContentLoader';
    }
    return 'PopularSpeciesNearYou';
  };

  const handleInfoButtonClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'NearbyPopularSpeciesSection',
        source_id: clickEventSourceId,
        type: 'read-more',
        page: currentPage,
      },
    });
    setIsInfoModalOpen(true);
  };

  return (
    <div className={twMerge('bg-lake-fog', className)} id={id}>
      <Container className="pt-16 pb-12">
        <ContinuousCarousel
          dataTestId={getTestId()}
          title={title}
          headingVariant="sm"
          description={
            region &&
            country && (
              <div className="flex items-center gap-1">
                <Text variant="sm" className="mb-0">
                  Result for {region}, {country}
                </Text>
                <Button
                  variant="textNone"
                  className="body-text-sm flex"
                  onClick={handleInfoButtonClick}
                >
                  <InfoIcon width={16} height={16} />
                </Button>
              </div>
            )
          }
          outerClass="mt-0!"
          showContentLoader
          isLoading={!clientHasRendered || isLoading}
          contentLoaderKey="species-near-you"
        >
          {speciesList.map(({ catchesCount, node: species }, index) => {
            if (species) {
              const images = getNodesFromConnection(species.exampleCatchWithImage?.post.images);
              const imageUrl = images[0]?.url || '';
              const imageWidth = images[0]?.width || 0;
              const imageHeight = images[0]?.height || 0;
              return (
                <SpeciesCard
                  className="rounded-xs md:rounded-xs"
                  key={`species-${species._id}`}
                  speciesId={species._id}
                  name={species.displayName}
                  imageUrl={imageUrl}
                  imageWidth={imageWidth}
                  imageHeight={imageHeight}
                  imageClassName="rounded-xs md:rounded-xs"
                  clickEventPosition={index}
                  clickEventSourceId={clickEventSourceId}
                  clickEventSource="NearbyPopularSpeciesSection"
                  numberCaught={catchesCount}
                />
              );
            }
            return <></>;
          })}
        </ContinuousCarousel>
        <LocationInfoModal
          isModalOpen={infoModalOpen}
          onCloseModal={() => {
            setIsInfoModalOpen(false);
          }}
          radius={RADIUS_OF_SPECIES_IN_KM}
        />
      </Container>
    </div>
  );
};
